/**
 * «منشی هوشمند پارسینا» — documents, vocabulary and the pure rules of the overlay
 * (PM-0080). DOM-free: the hook and the screen import from here, never the reverse.
 *
 * The secretary PROPOSES and the user COMMITS. `RUN_SECRETARY_COMMAND` writes
 * nothing; the two mutations below it are the operations that already existed for
 * the two writes the overlay can commit itself, and booking is committed by the
 * existing «ثبت نوبت» form, opened prefilled.
 */
import { clinicMinuteOfDay } from '../lib/clinicZone';

// A patient as the overlay shows one: who, told apart from namesakes, and — when a
// name lookup found them — how closely the heard name matched, and why.
const PATIENT = 'id name fileNumber age phoneTail isNew score reasons { kind heard stored }';

// What a proposal was made of. Handed back with an answer, so the BFF can rebuild the
// proposal without the model (refineSecretaryCommand); it trusts none of it.
const DRAFT = 'kind patientId patientName newPatient { name phone nationalId insurance insurerId } '
  + 'doctorId fromDate toDate time timeSaid when similar followUpKind followUpNote dueDate';

const RESULT = `
      outcome
      kind
      charged
      heardName
      patients { ${PATIENT} }
      doctors { doctorId name specialty }
      patient { ${PATIENT} }
      booking {
        patient { ${PATIENT} }
        doctor { doctorId name specialty }
        doctorAssumed
        fromMs
        toMs
        slots { dateTime }
        requestedAt
        requestedFree
        newPatient { name phone phoneHeard nationalId insurance insurerId insurerName missing }
      }
      arrival {
        appointmentId
        patient { ${PATIENT} }
        dateTime
        doctorName
        status
      }
      followUp {
        patient { ${PATIENT} }
        dueAt
        kind
        note
      }
      balance {
        patient { ${PATIENT} }
        outstanding
        openInvoices
        currency
      }
      daySummary { dateMs total scheduled arrived inVisit completed cancelled noShow }
      missingPermissions
      topic
      detail
      reason
      questions { kind blocking options { value label selected } }
      draft { ${DRAFT} }
`;

export const RUN_SECRETARY_COMMAND = `
  mutation RunSecretaryCommand($input: SecretaryCommandInput!) {
    runSecretaryCommand(input: $input) { ${RESULT} }
  }
`;

// An answer to one of the result's questions: a tap, or a mobile the desk confirmed
// after it was read back. Rebuilt in code — no model, no پارسه.
export const REFINE_SECRETARY_COMMAND = `
  query RefineSecretaryCommand($draft: SecretaryDraftInput!, $answer: SecretaryAnswerInput!) {
    refineSecretaryCommand(draft: $draft, answer: $answer) { ${RESULT} }
  }
`;

// The same operation پذیرش runs from the row («حاضر»).
export const SECRETARY_MARK_ARRIVED = `
  mutation SecretaryMarkArrived($appointmentId: ID!) {
    markArrived(appointmentId: $appointmentId) { id status }
  }
`;

// The same operation the patient record's «پیگیری» form runs.
export const SECRETARY_CREATE_FOLLOW_UP = `
  mutation SecretaryCreateFollowUp($input: FollowUpInput!) {
    createFollowUp(input: $input) { id }
  }
`;

/** Price-list action ids (clinicCreditPrices). */
export const PRICE_COMMAND = 'secretaryCommand';
export const PRICE_TRANSCRIPTION = 'transcription';

export const PHASE = Object.freeze({
  LISTENING: 'listening',
  PROCESSING: 'processing',
  RESULT: 'result',
  NO_CREDIT: 'noCredit',
  NO_MIC: 'noMic',
  ERROR: 'error',
});

/** Outcomes that end with something the user reads or acts on, laid out as the work sheet. */
export const OUT_OF_SCOPE_TOPICS = Object.freeze([
  'refund', 'void_invoice', 'take_payment', 'cashier_shift', 'doctor_settlement', 'payroll',
  'expense', 'inventory', 'roles_and_access', 'delete_record', 'send_message',
  'clinical_record', 'reschedule_or_cancel', 'other',
]);

export const FOLLOW_UP_KINDS = Object.freeze(['call', 'message', 'appointment', 'settlement', 'other']);

/** Why a candidate matched the heard name — the BFF's codes, each with its own copy. */
export const MATCH_REASONS = Object.freeze([
  'exact_name', 'same_sound', 'close_spelling', 'part_of_name', 'spacing', 'longer_name',
  'file_number', 'phone_tail', 'appointment_soon',
]);

/** «کدام‌یک؟» answers with a key: 1–5 pick a row, 0 says «بیمار جدید است». */
export const PICK_KEYS = Object.freeze(['1', '2', '3', '4', '5']);
export const NEW_PATIENT_KEY = '0';

// ── What the sentence left out: follow-up questions ─────────────────────────────
//
// The BFF works out what a proposal is missing and says so as `questions`, one kind
// each. A BLOCKING one is asked: which doctor, a new patient's mobile and insurer, the
// day of a پیگیری. «برای چه زمانی؟» is an OFFER — the first free times stand while it
// is open. One question is on screen at a time, the blocking ones first.

export const QUESTION_KINDS = Object.freeze(['doctor', 'phone', 'insurance', 'when', 'followUpDate']);
/** The questions «رد کردن، در فرم وارد می‌کنم» may close: the form asks for these itself. */
export const SKIPPABLE_QUESTIONS = Object.freeze(['phone', 'insurance']);
export const SKIP_KEY = '0';
/** The choice codes the BFF sends without a label; the panel words them itself. */
export const WHEN_CHOICES = Object.freeze(['first', 'today', 'tomorrow', 'next_week']);
export const FOLLOW_UP_CHOICES = Object.freeze(['tomorrow', 'next_week', 'next_month']);

/** The question to show now — the first blocking one not yet closed, else the offer. */
export function openQuestion(result, closed = new Set()) {
  const open = (result?.questions || []).filter((q) => QUESTION_KINDS.includes(q?.kind) && !closed.has(q.kind));
  return open.find((q) => q.blocking) || open[0] || null;
}

/**
 * Where opening a result goes (the record button, PM-0080). The secretary's own
 * window opens only when something must be answered or chosen first:
 *   'form'     — a booking with nothing blocking: «ثبت نوبت», filled in;
 *   'formDay'  — no free time where it was asked: «ثبت نوبت» on that day;
 *   'navigate' — «باز کردن پرونده»: the record itself;
 *   'window'   — everything else.
 * `closed`: the questions the desk skipped, which the form asks instead.
 */
export function routeOf(result, closed = new Set()) {
  if (!result) return 'window';
  if (result.outcome === 'PROPOSAL' && result.kind === 'book_appointment' && result.booking) {
    const question = openQuestion(result, closed);
    return question?.blocking ? 'window' : 'form';
  }
  if (result.outcome === 'NO_SLOT' && result.booking) return 'formDay';
  if (result.outcome === 'NAVIGATE' && result.patient?.id) return 'navigate';
  return 'window';
}

/** How many blocking questions are still open — for «۱ از ۲». */
export function openBlockingCount(result, closed = new Set()) {
  return (result?.questions || [])
    .filter((q) => QUESTION_KINDS.includes(q?.kind) && q.blocking && !closed.has(q.kind)).length;
}

/** The answer field each question fills (SecretaryAnswerInput). */
export function answerFor(kind, value) {
  if (kind === 'doctor') return { doctorId: String(value) };
  if (kind === 'phone') return { phone: String(value) };
  if (kind === 'insurance') return { insurerId: String(value) };
  if (kind === 'when') return { when: String(value) };
  if (kind === 'followUpDate') return { followUpWhen: String(value) };
  return null;
}

const DRAFT_FIELDS = ['kind', 'patientId', 'patientName', 'doctorId', 'fromDate', 'toDate', 'time', 'timeSaid',
  'when', 'similar', 'followUpKind', 'followUpNote', 'dueDate'];
const DRAFT_PATIENT_FIELDS = ['name', 'phone', 'nationalId', 'insurance', 'insurerId'];

/** A result's draft as SecretaryDraftInput — its own fields and nothing else. */
export function draftInput(draft) {
  if (!draft) return null;
  const out = {};
  for (const k of DRAFT_FIELDS) out[k] = draft[k] ?? null;
  if (draft.newPatient) {
    out.newPatient = {};
    for (const k of DRAFT_PATIENT_FIELDS) out.newPatient[k] = draft.newPatient[k] ?? null;
  } else {
    out.newPatient = null;
  }
  return out;
}

/**
 * The sheet after an answer. The answer is about the SAME patient, so what the name
 * lookup found stays on it («بیمار دیگری است؟», the closest names), and what the
 * command itself cost stays stated: an answer adds nothing to it.
 */
export function mergeRefined(previous, next) {
  if (!previous) return next;
  return {
    ...next,
    charged: Boolean(previous.charged || next.charged),
    heardName: next.heardName || previous.heardName || null,
    patients: next.patients?.length ? next.patients : (previous.patients || null),
  };
}

// i18n-exempt: digit CHARACTERS folded to ASCII, not copy.
const DIGITS = { persian: '۰۱۲۳۴۵۶۷۸۹', arabic: '٠١٢٣٤٥٦٧٨٩' };
const foldDigits = (text) => String(text || '').replace(/[۰-۹٠-٩]/g, (c) => {
  const p = DIGITS.persian.indexOf(c);
  return String(p >= 0 ? p : DIGITS.arabic.indexOf(c));
});

/** Spoken text, folded so two spellings of one word compare equal. */
export function foldSpoken(text) {
  return foldDigits(text)
    .replace(/[\u064B-\u065F\u0670]/g, '') // harakat, and the hamza of «هفتهٔ»
    .replace(/[يى]/g, 'ی').replace(/ك/g, 'ک').replace(/[ةۀ]/g, 'ه').replace(/[أإآ]/g, 'ا').replace(/ؤ/g, 'و') // i18n-exempt: CHARACTERS folded, not copy
    .replace(/\u200c/g, ' ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

// Persian number words, as a mobile is dictated: «صفر نهصد و سیزده، سیصد و پنجاه و یک».
// i18n-exempt: words PARSED out of speech, never rendered.
const NUMBER_WORDS = new Map(Object.entries({
  صفر: 0, یک: 1, دو: 2, سه: 3, چهار: 4, پنج: 5, شش: 6, شیش: 6, هفت: 7, هشت: 8, نه: 9,
  ده: 10, یازده: 11, دوازده: 12, سیزده: 13, چهارده: 14, پانزده: 15, پونزده: 15, شانزده: 16, شونزده: 16,
  هفده: 17, هیفده: 17, هجده: 18, هیجده: 18, نوزده: 19,
  بیست: 20, سی: 30, چهل: 40, پنجاه: 50, شصت: 60, هفتاد: 70, هشتاد: 80, نود: 90,
  صد: 100, یکصد: 100, دویست: 200, سیصد: 300, چهارصد: 400, پانصد: 500, پونصد: 500,
  ششصد: 600, شیشصد: 600, هفتصد: 700, هشتصد: 800, نهصد: 900,
}));
// What a number fills — hundreds 3, tens 2, units 1 — and so what may still follow it.
const placeOf = (v) => (v >= 100 ? 3 : v >= 10 ? 2 : 1);
const roomAfter = (v) => (v >= 100 ? 2 : v >= 20 ? 1 : 0);

/**
 * The digits in a spoken answer, in the order they were said: digit runs as they
 * are, and number words joined the way a mobile is dictated — «صفر نهصد و سیزده،
 * سیصد و پنجاه و یک، پنجاه و یک، چهل و دو» is 09133515142. A pause (a comma) or any
 * other word ends a group. Never a guess at what was meant: the desk reads it back.
 */
export function digitsFromSpeech(text) {
  const tokens = foldDigits(text)
    .replace(/[يى]/g, 'ی').replace(/ك/g, 'ک') // i18n-exempt: CHARACTERS folded, not copy
    .replace(/\u200c/g, ' ')
    .match(/\d+|[\p{L}]+|[^\s\p{L}\d]/gu) || [];
  let out = '';
  let group = null; // { value, room }
  const flush = () => { if (group) { out += String(group.value); group = null; } };
  for (const token of tokens) {
    if (/^\d+$/.test(token)) { flush(); out += token; continue; }
    if (token === 'و') continue; // i18n-exempt: «و» joins spoken number words, not copy
    const v = NUMBER_WORDS.get(token);
    if (v === undefined) { flush(); continue; }
    // Zero is always a digit of its own; anything else joins a group with room for it.
    if (group && v !== 0 && placeOf(v) <= group.room) {
      group = { value: group.value + v, room: v < 20 ? 0 : roomAfter(v) };
      continue;
    }
    flush();
    group = v === 0 ? { value: 0, room: 0 } : { value: v, room: roomAfter(v) };
  }
  flush();
  return out;
}

/** Digits in the groups a mobile is read back in: 0913 351 5142. */
export function digitGroups(digits) {
  const d = String(digits || '');
  if (/^09\d{9}$/.test(d)) return [d.slice(0, 4), d.slice(4, 7), d.slice(7)];
  if (/^9\d{9}$/.test(d)) return [d.slice(0, 3), d.slice(3, 6), d.slice(6)];
  const out = [];
  for (let i = 0; i < d.length; i += 3) out.push(d.slice(i, i + 3));
  if (out.length > 1 && out[out.length - 1].length === 1) out[out.length - 2] += out.pop();
  return out;
}

/**
 * The quick answer a SPOKEN answer names — or null when it names none, or more than
 * one. `exact`: the whole answer must be the option («فردا»), because «فردا ساعت ۵»
 * says more than the chip can carry and goes back to the planner instead.
 * `ignore`: words that do not tell options apart («دکتر», «بیمه»).
 */
export function matchSpokenOption(spoken, options, { exact = false, ignore = [] } = {}) {
  const drop = new Set(ignore.map(foldSpoken));
  const words = (text) => foldSpoken(text).split(' ').filter((w) => w && !drop.has(w));
  const said = words(spoken);
  if (!said.length) return null;
  const saidText = said.join(' ');
  const hits = (options || []).filter((o) => {
    const label = words(o?.label);
    if (!label.length) return false;
    if (exact) return label.join(' ') === saidText;
    return saidText.includes(label.join(' ')) || label.some((w) => w.length >= 3 && said.includes(w));
  });
  return hits.length === 1 ? hits[0] : null;
}

/** The patient a result is about, whichever payload carries them. */
export function resultPatient(result) {
  return result?.booking?.patient || result?.arrival?.patient || result?.followUp?.patient
    || result?.balance?.patient || result?.patient || null;
}

/**
 * The other patients the lookup found — what «بیمار دیگری است؟» offers. Empty for a
 * patient no name lookup found, and for a list with nobody else on it.
 */
export function otherCandidates(result) {
  const current = resultPatient(result);
  return (result?.patients || []).filter((p) => p?.id && p.id !== current?.id);
}

const CREDIT_CODES = new Set(['INSUFFICIENT_CREDIT', 'INSUFFICIENT_CREDITS']);

/** Is this failure «پارسه کافی نیست»? The one failure with a fix the user owns. */
export function isCreditRefusal(err) {
  return CREDIT_CODES.has(err?.extensions?.code);
}

/**
 * The least پارسه a spoken command can cost: one started minute of transcription
 * plus the command itself. Null when either price is unknown — the server then
 * decides, and the screen says «نامشخص» by saying nothing about a number.
 */
export function minimumCost(priceOf) {
  const minute = priceOf?.(PRICE_TRANSCRIPTION);
  const command = priceOf?.(PRICE_COMMAND);
  if (!Number.isFinite(minute) || !Number.isFinite(command)) return null;
  return minute + command;
}

/** Known balance below the known minimum. Anything unknown is NOT a refusal. */
export function cannotAfford(balance, priceOf) {
  const min = minimumCost(priceOf);
  return Number.isFinite(balance) && min !== null && balance < min;
}

/** "HH:MM" on the clinic's clock — the label the booking form's slot grid is keyed by. */
export function slotLabelOf(dateTimeMs) {
  const minutes = clinicMinuteOfDay(Number(dateTimeMs));
  if (!Number.isFinite(minutes)) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}

/** "MM:SS" for the recording clock, ASCII — the caller renders it through formatDigits. */
export function clockOf(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
}

/**
 * What the booking form needs to open prefilled for one proposed slot. A patient the
 * clinic has no record of travels as `newPatient` — the form opens on «بیمار جدید»
 * with the heard name, and its own submit registers them.
 */
export function bookingHandoff(booking, slotIndex = 0) {
  if (!booking) return null;
  const slot = booking.slots?.[slotIndex] || null;
  const isNew = !!booking.patient?.isNew;
  return {
    doctorId: booking.doctor.doctorId,
    doctorAssumed: booking.doctorAssumed || null,
    patient: isNew ? null : { id: booking.patient.id, name: booking.patient.name, phone: '' },
    // Everything that was SAID about a new patient — the form fills it in and asks for the rest.
    newPatient: isNew ? {
      name: booking.patient.name,
      phone: booking.newPatient?.phone || null,
      nationalId: booking.newPatient?.nationalId || null,
      insurerId: booking.newPatient?.insurerId || null,
    } : null,
    // No slot (NO_SLOT) → the form opens on the first day of the window, unpinned.
    initialDate: slot ? slot.dateTime : booking.fromMs,
    initialSlotLabel: slot ? slotLabelOf(slot.dateTime) : null,
  };
}
